import {
  addDecimals,
  compareDecimalStrings,
  isDecimalString,
  MONEY_SCALE,
  normalizeDecimal,
  percentageOfDecimal,
  subtractDecimals,
  trimTrailingZeros,
} from "@/shared/utils/decimal";

import {
  EconomicModel,
  PaymentDirection,
  PaymentFeeKind,
  type CorridorCoverage,
  type CorridorOrgOverride,
  type FeeComputation,
  type FeeRowStatus,
  type FeeRuleGroup,
  type FeeScope,
  type FeeVersionRow,
  type PaymentFee,
  type ProviderRailRoute,
} from "../types/paymentFees.types";
import type { PaymentTax } from "../types/paymentTaxes.types";
import { applyTaxes, resolveTaxes } from "./taxMath";

/**
 * Client-side port of the runtime's fee engine — `PaymentFeeService.resolve/compute` and
 * `FeeAssemblyService.build` in coloca-nest. It exists for three things the API cannot answer:
 *
 * 1. **Which row wins.** `POST` never closes the previous version's `valid_to`, so several rows of one scope
 *    stay `is_active`. Only by replaying the resolver can the UI label one row `ACTIVE` and the rest
 *    `SUPERSEDED` instead of showing five "active" prices for the same corridor.
 * 2. **Coverage.** A corridor with no resolvable commission _and_ cost refuses every payin/payout (`404
 *    no_fee_config`). There is no coverage endpoint.
 * 3. **The authoring preview.** There is no simulate endpoint, so the dialog computes the fee itself.
 *
 * Because of (3) the arithmetic must agree with the backend to the last of 8 decimals: all of it runs through
 * the BigInt helpers in `@/shared/utils/decimal`, never `parseFloat`.
 */

const SCOPE_DIMENSIONS = [
  "provider_code",
  "organization_id",
  "rail",
  "country",
  "currency",
  "direction",
] as const satisfies readonly (keyof FeeScope)[];

export function feeScopeOf(fee: PaymentFee): FeeScope {
  return {
    provider_code: fee.provider_code ?? null,
    organization_id: fee.organization_id ?? null,
    rail: fee.rail ?? null,
    country: fee.country ?? null,
    currency: fee.currency ?? null,
    direction: fee.direction ?? null,
  };
}

/** Stable identity of a version stack: the fee kind plus the six scope columns, in the resolver's order. */
export function scopeKey(feeKind: PaymentFeeKind, scope: FeeScope): string {
  return [feeKind, ...SCOPE_DIMENSIONS.map((dimension) => scope[dimension] ?? "")].join("|");
}

/** The resolver's primary ranking: how many scope columns this rule pins down (0–6). */
export function specificityOf(scope: FeeScope): number {
  return SCOPE_DIMENSIONS.filter((dimension) => scope[dimension] != null).length;
}

/** `is_active` AND not soft-deleted AND inside its validity window — the SQL candidate filter. */
export function isFeeLive(fee: PaymentFee, now: number): boolean {
  if (!fee.is_active || fee.deleted_at) return false;
  if (Date.parse(fee.valid_from) > now) return false;
  return fee.valid_to == null || Date.parse(fee.valid_to) > now;
}

/**
 * Derived lifecycle state. `SUPERSEDED` is the important one: the row is still live in the database, but a
 * later-effective sibling of the same scope outranks it, so the resolver will never pick it.
 */
export function feeStatusOf(fee: PaymentFee, scopeRows: PaymentFee[], now: number): FeeRowStatus {
  if (!fee.is_active) return "DISABLED";
  if (fee.valid_to != null && Date.parse(fee.valid_to) <= now) return "EXPIRED";
  if (Date.parse(fee.valid_from) > now) return "SCHEDULED";
  const live = scopeRows
    .filter((row) => isFeeLive(row, now))
    .sort((a, b) => Date.parse(b.valid_from) - Date.parse(a.valid_from));
  return live[0]?.id === fee.id ? "ACTIVE" : "SUPERSEDED";
}

/**
 * Groups rows by their exact scope tuple and orders the groups the way the resolver evaluates them: most
 * specific first, ties broken alphabetically so the list is stable across refreshes.
 */
export function groupFeesByScope(fees: PaymentFee[], now: number): FeeRuleGroup[] {
  const buckets = new Map<string, PaymentFee[]>();
  for (const fee of fees) {
    if (fee.deleted_at) continue;
    const key = scopeKey(fee.fee_kind, feeScopeOf(fee));
    const bucket = buckets.get(key);
    if (bucket) bucket.push(fee);
    else buckets.set(key, [fee]);
  }

  const groups: FeeRuleGroup[] = [];
  for (const [key, bucket] of buckets) {
    const ordered = [...bucket].sort((a, b) => Date.parse(b.valid_from) - Date.parse(a.valid_from));
    const rows: FeeVersionRow[] = ordered.map((fee) => ({
      fee,
      status: feeStatusOf(fee, bucket, now),
    }));
    // No live row (all scheduled / expired / disabled) still needs something in the header — prefer the next
    // change, then simply the newest row, so the group is never blank.
    const winner =
      rows.find((row) => row.status === "ACTIVE") ??
      rows.find((row) => row.status === "SCHEDULED") ??
      rows[0];
    const scope = feeScopeOf(ordered[0]);
    groups.push({
      key,
      scope,
      feeKind: ordered[0].fee_kind,
      specificity: specificityOf(scope),
      rows,
      winner,
    });
  }

  return groups.sort((a, b) => b.specificity - a.specificity || a.key.localeCompare(b.key));
}

export interface ResolveFeeQuery extends Partial<FeeScope> {
  feeKind: PaymentFeeKind;
}

/**
 * Port of `PaymentFeeService.resolve` + `applyScope`. Per dimension: a value supplied by the operation
 * matches rows that are NULL there or equal to it; a dimension the operation does not supply matches only
 * NULL rows. That asymmetry is what makes a `COLOCA_COMMISSION` row with a provider set — or a
 * `PROVIDER_COST` row with an organization set — permanently unreachable.
 */
export function resolveFee(query: ResolveFeeQuery, fees: PaymentFee[], now: number): PaymentFee | null {
  const candidates = fees.filter((fee) => {
    if (fee.fee_kind !== query.feeKind) return false;
    if (!isFeeLive(fee, now)) return false;
    return SCOPE_DIMENSIONS.every((dimension) => {
      const asked = query[dimension] ?? null;
      const rowValue = fee[dimension] ?? null;
      return asked == null ? rowValue == null : rowValue == null || rowValue === asked;
    });
  });
  if (candidates.length === 0) return null;

  return candidates.sort(
    (a, b) =>
      specificityOf(feeScopeOf(b)) - specificityOf(feeScopeOf(a)) ||
      Date.parse(b.valid_from) - Date.parse(a.valid_from),
  )[0];
}

/**
 * Tier members are the runtime's one deviation from decimal strings: JSON numbers. `String(n)` serializes
 * values below 1e-6 or at/above 1e21 in scientific notation, which the decimal helpers reject — and a
 * threshold that silently collapsed to "0" would apply its tier to EVERY amount, diverging from the backend.
 * `toFixed` stays plain-decimal across the whole `numeric(20,8)` domain; a member it cannot represent
 * resolves to null so the caller skips the tier instead of mispricing it.
 */
function tierMemberToDecimal(value: number): string | null {
  if (!Number.isFinite(value) || value < 0) return null;
  const fixed = value.toFixed(MONEY_SCALE);
  return isDecimalString(fixed) ? fixed : null;
}

/**
 * Same conversion for form seeding and inline display, trimmed of trailing zeros ("1.50000000" → "1.5"). An
 * unrepresentable member falls back to `String(value)` — shown raw and refused by form validation, never
 * silently rewritten into a different number.
 */
export function tierMemberToInput(value: number): string {
  const decimal = tierMemberToDecimal(value);
  if (decimal == null) return String(value);
  return trimTrailingZeros(decimal);
}

/**
 * Port of `PaymentFeeService.compute`: `fixed + amount·percentage/100`, then clamped to `[min_fee, max_fee]`.
 * A matching tier REPLACES `fixed` and `percentage` — it does not add to them — and an amount below the
 * lowest threshold matches no tier, falling back to the base pair.
 *
 * The tier lookup compares exactly (BigInt) where the backend compares `Number(amount) >= threshold`; the two
 * agree for every amount `numeric(20,8)` can hold.
 */
export function computeFee(
  fee: Pick<PaymentFee, "fixed" | "percentage" | "min_fee" | "max_fee" | "tiers">,
  amount: string,
): FeeComputation | null {
  if (!isDecimalString(amount)) return null;

  let fixed = normalizeMoney(fee.fixed);
  let percentage = normalizeMoney(fee.percentage);
  let tierIndex: number | null = null;

  if (fee.tiers && fee.tiers.length > 0) {
    const ascending = [...fee.tiers].sort((a, b) => a.threshold - b.threshold);
    // Backend order: on a duplicated threshold the tier stored first wins.
    const descending = [...fee.tiers].sort((a, b) => b.threshold - a.threshold);
    for (const tier of descending) {
      const threshold = tierMemberToDecimal(tier.threshold);
      const tierFixed = tierMemberToDecimal(tier.fixed);
      const tierPercentage = tierMemberToDecimal(tier.percentage);
      // A tier with an unrepresentable member is skipped entirely — applying it as zero would price every
      // amount into it (a zero threshold matches everything).
      if (threshold == null || tierFixed == null || tierPercentage == null) continue;
      if (compareDecimalStrings(amount, threshold) >= 0) {
        fixed = tierFixed;
        percentage = tierPercentage;
        tierIndex = ascending.indexOf(tier) + 1;
        break;
      }
    }
  }

  let total = addDecimals(fixed, percentageOfDecimal(amount, percentage));
  const min = normalizeMoney(fee.min_fee);
  const max = fee.max_fee == null || fee.max_fee === "" ? null : normalizeMoney(fee.max_fee);

  let clampedToMin = false;
  let clampedToMax = false;
  if (compareDecimalStrings(total, min) < 0) {
    total = min;
    clampedToMin = true;
  }
  if (max != null && compareDecimalStrings(total, max) > 0) {
    total = max;
    clampedToMax = true;
  }

  return { total, tierIndex, clampedToMin, clampedToMax, min, max };
}

/** Empty / malformed money coming from a half-typed form field must read as zero, never crash the preview. */
function normalizeMoney(value: string | null | undefined): string {
  if (value == null || value === "" || value === ".") return normalizeDecimal("0");
  return isDecimalString(value) ? normalizeDecimal(value) : normalizeDecimal("0");
}

export function isZeroDecimal(value: string | null | undefined): boolean {
  return compareDecimalStrings(normalizeMoney(value), "0") === 0;
}

export function isZeroPrice(fee: Pick<PaymentFee, "fixed" | "percentage" | "min_fee" | "tiers">): boolean {
  return (
    isZeroDecimal(fee.fixed) &&
    isZeroDecimal(fee.percentage) &&
    isZeroDecimal(fee.min_fee) &&
    (fee.tiers ?? []).every((tier) => tier.fixed === 0 && tier.percentage === 0)
  );
}

/** A band of ONE operation's amount; `to` is exclusive and `null` means "and above". */
export interface FeeAmountRange {
  from: string;
  to: string | null;
  fixed: string;
  percentage: string;
  isBase: boolean;
}

export function tierRanges(fee: Pick<PaymentFee, "fixed" | "percentage" | "tiers">): FeeAmountRange[] {
  const baseFixed = normalizeMoney(fee.fixed);
  const basePercentage = normalizeMoney(fee.percentage);
  const seen = new Set<string>();
  const tiers = [...(fee.tiers ?? [])]
    .map((tier) => ({
      threshold: tierMemberToDecimal(tier.threshold),
      fixed: tierMemberToDecimal(tier.fixed),
      percentage: tierMemberToDecimal(tier.percentage),
    }))
    .filter(
      (tier): tier is { threshold: string; fixed: string; percentage: string } =>
        tier.threshold != null && tier.fixed != null && tier.percentage != null,
    )
    .sort((a, b) => compareDecimalStrings(a.threshold, b.threshold))
    .filter((tier) => {
      if (seen.has(tier.threshold)) return false;
      seen.add(tier.threshold);
      return true;
    });

  if (tiers.length === 0) {
    return [
      { from: normalizeDecimal("0"), to: null, fixed: baseFixed, percentage: basePercentage, isBase: true },
    ];
  }

  const ranges: FeeAmountRange[] = [];
  if (compareDecimalStrings(tiers[0].threshold, "0") > 0) {
    ranges.push({
      from: normalizeDecimal("0"),
      to: tiers[0].threshold,
      fixed: baseFixed,
      percentage: basePercentage,
      isBase: true,
    });
  }
  tiers.forEach((tier, index) => {
    ranges.push({
      from: tier.threshold,
      to: tiers[index + 1]?.threshold ?? null,
      fixed: tier.fixed,
      percentage: tier.percentage,
      isBase: false,
    });
  });
  return ranges;
}

export function rangeForAmount(ranges: FeeAmountRange[], amount: string): FeeAmountRange | null {
  if (!isDecimalString(amount)) return null;
  return (
    ranges.find(
      (range) =>
        compareDecimalStrings(amount, range.from) >= 0 &&
        (range.to == null || compareDecimalStrings(amount, range.to) < 0),
    ) ?? null
  );
}

/**
 * Model-aware total the client is charged: `commission + cost` when passed through, commission alone when
 * absorbed.
 */
export function clientTotal(
  commission: string | null,
  cost: string | null,
  model: EconomicModel,
): string | null {
  if (commission == null) return null;
  if (model === EconomicModel.ABSORBED) return commission;
  return cost == null ? null : addDecimals(commission, cost);
}

/**
 * What Coloca keeps. Under `PASS_THROUGH` the client pays the provider too, so the margin is the whole
 * commission; under `ABSORBED` the cost comes out of it and the result **can be negative** — nothing in the
 * backend prevents a loss-making corridor.
 */
export function colocaMargin(
  commission: string | null,
  cost: string | null,
  model: EconomicModel,
): string | null {
  if (commission == null) return null;
  if (model === EconomicModel.PASS_THROUGH) return commission;
  return cost == null ? null : subtractDecimals(commission, cost);
}

const BREAKEVEN_MAX_CENTS = 1_000_000_000; // 10,000,000.00 — above any real single operation.

/** Integer minor units → decimal string. Integer maths on `number` is exact well below 2^53. */
function centsToDecimal(cents: number): string {
  const whole = Math.floor(cents / 100);
  return `${whole}.${String(cents % 100).padStart(2, "0")}`;
}

/** First cent at which a tier threshold starts applying, or null when it lies outside the probed range. */
function tierThresholdCents(threshold: number): number | null {
  if (!Number.isFinite(threshold) || threshold < 0) return null;
  const cents = Math.ceil(threshold * 100 - 1e-9);
  return cents >= 1 && cents <= BREAKEVEN_MAX_CENTS ? cents : null;
}

export type BreakevenOutcome =
  /** No probed amount is refused — nothing to warn about. */
  | { kind: "ALL_ACCEPTED" }
  /** Everything below `gross` is refused, everything from there up is accepted. The monotone case. */
  | { kind: "THRESHOLD"; gross: string }
  /**
   * Refusals exist ABOVE the smallest accepted amount — a tier raises the fee past the gross partway up, so
   * the refused set is a band, not a floor. No single figure describes it honestly.
   */
  | { kind: "SOME_REFUSED" }
  /** No amount in the probed range clears the fees — the corridor would refuse every operation. */
  | { kind: "ALL_REFUSED" };

/**
 * Where the runtime's `net <= 0` guards start accepting operations, probed in integer cents.
 *
 * Mirrors two distinct backend guards: a payout with `net <= 0` is a 400, and a payin is persisted `REJECTED`
 * with no credit when `net <= 0` **or** `gross − provider_cost <= 0` (the suspense leg would go non-positive
 * before the net does when the provider cost alone eats the gross).
 *
 * Refusal is NOT monotone in the gross once tiers exist — a higher tier can raise the fixed fee and re-refuse
 * amounts above a formerly accepted range — so one bisection over the whole range is unsound. Instead the
 * range is cut at every tier threshold of both rules: inside one segment the fee pair is constant and the net
 * is monotone (slope ≥ 0 for percentages ≤ 100), so each segment is checked at its edges and bisected only
 * when the refused→accepted boundary lies inside it. The result is the smallest accepted gross overall.
 *
 * Returns `null` when the question is unanswerable rather than answered badly: no commission rule, or a
 * PASS_THROUGH corridor without a cost rule — the counterpart warning owns that case.
 */
export function breakevenGross(
  commission: Pick<PaymentFee, "fixed" | "percentage" | "min_fee" | "max_fee" | "tiers"> | null,
  cost: Pick<PaymentFee, "fixed" | "percentage" | "min_fee" | "max_fee" | "tiers"> | null,
  model: EconomicModel,
  direction: PaymentDirection | null,
): BreakevenOutcome | null {
  if (commission == null) return null;
  if (model === EconomicModel.PASS_THROUGH && cost == null) return null;

  const refused = (cents: number): boolean => {
    const gross = centsToDecimal(cents);
    const commissionTotal = computeFee(commission, gross)?.total ?? null;
    if (commissionTotal == null) return true;
    const costTotal = cost ? (computeFee(cost, gross)?.total ?? null) : null;
    const fees = clientTotal(commissionTotal, costTotal, model);
    if (fees == null) return true;
    if (compareDecimalStrings(subtractDecimals(gross, fees), "0") <= 0) return true;
    if (direction === PaymentDirection.PAYIN && costTotal != null) {
      if (compareDecimalStrings(subtractDecimals(gross, costTotal), "0") <= 0) return true;
    }
    return false;
  };

  const boundaries = new Set<number>([1]);
  for (const rule of [commission, cost]) {
    for (const tier of rule?.tiers ?? []) {
      const cents = tierThresholdCents(tier.threshold);
      if (cents != null) boundaries.add(cents);
    }
  }
  const starts = [...boundaries].sort((a, b) => a - b);
  const segments = starts.map((start, index) => ({
    start,
    end: index + 1 < starts.length ? starts[index + 1] - 1 : BREAKEVEN_MAX_CENTS,
  }));

  let smallestAccepted: number | null = null;
  let firstSegment = 0;
  for (let index = 0; index < segments.length; index += 1) {
    const { start, end } = segments[index];
    if (end < start) continue;
    if (!refused(start)) {
      smallestAccepted = start;
      firstSegment = index;
      break;
    }
    if (refused(end)) continue;
    let low = start;
    let high = end;
    while (high - low > 1) {
      const middle = Math.floor((low + high) / 2);
      if (refused(middle)) low = middle;
      else high = middle;
    }
    smallestAccepted = high;
    firstSegment = index;
    break;
  }

  if (smallestAccepted == null) return { kind: "ALL_REFUSED" };

  // Any refusal above the smallest accepted amount means the refused set is a band. Both edges of every
  // remaining segment are probed: the fee pair is constant inside one, so a refusal there shows at an edge.
  for (let index = firstSegment; index < segments.length; index += 1) {
    const { start, end } = segments[index];
    const from = Math.max(start, smallestAccepted);
    if (end < from) continue;
    if (refused(from) || refused(end)) return { kind: "SOME_REFUSED" };
  }

  if (smallestAccepted === 1) return { kind: "ALL_ACCEPTED" };
  return { kind: "THRESHOLD", gross: centsToDecimal(smallestAccepted) };
}

/**
 * Coverage + economics of every active route at its currency's example amount. Only routes the catalog
 * actually returns appear — a corridor is never invented, and a missing side renders as "not configured",
 * never as `0.00`.
 *
 * `organizationId` is a SCOPE, not a filter: the resolver already falls back to the global rule when the
 * organization has none, so the figures are what that client is actually charged, and `commissionSource` says
 * which of the two produced them. Passing `null` resolves the platform default — the same view every
 * organization without an override gets.
 */
export function buildCorridorCoverage(
  routes: ProviderRailRoute[],
  fees: PaymentFee[],
  exampleAmountFor: (currency: string) => string,
  now: number,
  organizationId: string | null = null,
  taxRows: PaymentTax[] = [],
): CorridorCoverage[] {
  const overridesByRoute = organizationId ? null : orgOverridesByRoute(routes, fees, now);

  return routes.map((route) => {
    const exampleAmount = exampleAmountFor(route.currency);
    const commissionQuery = feeQueryFor(route, PaymentFeeKind.COLOCA_COMMISSION);
    const commission = resolveFee(
      feeQueryFor(route, PaymentFeeKind.COLOCA_COMMISSION, organizationId),
      fees,
      now,
    );
    // What the override replaces. In the global scope it IS the resolved rule, so the second replay is skipped.
    const globalCommission = organizationId ? resolveFee(commissionQuery, fees, now) : commission;
    const cost = resolveFee(feeQueryFor(route, PaymentFeeKind.PROVIDER_COST), fees, now);

    const commissionComputation = commission ? computeFee(commission, exampleAmount) : null;
    const costComputation = cost ? computeFee(cost, exampleAmount) : null;
    const commissionAmount = commissionComputation?.total ?? null;
    const costAmount = costComputation?.total ?? null;
    const taxes = applyTaxes(
      resolveTaxes(
        taxRows,
        {
          country: route.country,
          rail: route.rail,
          direction: route.direction,
          currency: route.currency,
          organizationId,
        },
        now,
      ),
      {
        grossAmount: exampleAmount,
        commission: commissionAmount ?? "0",
        currency: route.currency,
        ...(route.direction === PaymentDirection.PAYOUT
          ? { payoutFees: clientTotal(commissionAmount, costAmount, route.economic_model) ?? "0" }
          : {}),
      },
    );
    const clientPays = clientTotal(commissionAmount, costAmount, route.economic_model);
    const margin = colocaMargin(commissionAmount, costAmount, route.economic_model);

    return {
      route,
      exampleAmount,
      commission,
      cost,
      commissionAmount,
      costAmount,
      commissionComputation,
      costComputation,
      commissionSource: commission ? (commission.organization_id ? "ORGANIZATION" : "GLOBAL") : null,
      globalCommission,
      globalCommissionAmount: globalCommission
        ? (computeFee(globalCommission, exampleAmount)?.total ?? null)
        : null,
      ownRuleShadowed: organizationId ? shadowedOwnRule(route, organizationId, commission, fees, now) : null,
      orgOverrides: overridesByRoute?.get(route.id) ?? [],
      hasScheduledChange: hasScheduledChangeFor(route, organizationId, fees, now),
      taxes: taxes.lines,
      clientPays: clientPays == null ? null : addDecimals(clientPays, taxes.clientTotal),
      margin: margin == null ? null : subtractDecimals(margin, taxes.colocaTotal),
      status: commission && cost ? "PRICED" : commission || cost ? "PARTIAL" : "UNPRICED",
    };
  });
}

/** Only organizations whose own rule wins: a row outranked by a more specific general rule does not apply. */
export function orgOverridesByRoute(
  routes: ProviderRailRoute[],
  fees: PaymentFee[],
  now: number,
): Map<string, CorridorOrgOverride[]> {
  const organizationIds = [
    ...new Set(
      fees
        .filter(
          (fee) =>
            fee.fee_kind === PaymentFeeKind.COLOCA_COMMISSION && fee.organization_id && isFeeLive(fee, now),
        )
        .map((fee) => fee.organization_id as string),
    ),
  ];
  const result = new Map<string, CorridorOrgOverride[]>();
  for (const route of routes) {
    const overrides: CorridorOrgOverride[] = [];
    for (const organizationId of organizationIds) {
      const fee = resolveFee(feeQueryFor(route, PaymentFeeKind.COLOCA_COMMISSION, organizationId), fees, now);
      if (fee?.organization_id === organizationId) overrides.push({ organizationId, fee });
    }
    result.set(route.id, overrides);
  }
  return result;
}

function shadowedOwnRule(
  route: ProviderRailRoute,
  organizationId: string,
  winner: PaymentFee | null,
  fees: PaymentFee[],
  now: number,
): PaymentFee | null {
  if (winner?.organization_id === organizationId) return null;
  return (
    fees.find(
      (fee) =>
        fee.fee_kind === PaymentFeeKind.COLOCA_COMMISSION &&
        fee.organization_id === organizationId &&
        isFeeLive(fee, now) &&
        routesCoveredBy(PaymentFeeKind.COLOCA_COMMISSION, feeScopeOf(fee), [route]).length > 0,
    ) ?? null
  );
}

function hasScheduledChangeFor(
  route: ProviderRailRoute,
  organizationId: string | null,
  fees: PaymentFee[],
  now: number,
): boolean {
  return fees.some((fee) => {
    if (!fee.is_active || fee.deleted_at || Date.parse(fee.valid_from) <= now) return false;
    if (fee.organization_id && fee.organization_id !== organizationId) return false;
    return routesCoveredBy(fee.fee_kind, feeScopeOf(fee), [route]).length > 0;
  });
}

/** A commission query never carries a provider and a cost query never carries an organization. */
export function feeQueryFor(
  route: ProviderRailRoute,
  feeKind: PaymentFeeKind,
  organizationId: string | null = null,
): ResolveFeeQuery {
  const corridor = {
    feeKind,
    rail: route.rail,
    country: route.country,
    currency: route.currency,
    direction: route.direction,
  };
  if (feeKind === PaymentFeeKind.PROVIDER_COST) return { ...corridor, provider_code: route.provider_code };
  return organizationId ? { ...corridor, organization_id: organizationId } : corridor;
}

export function corridorScopeOf(
  route: ProviderRailRoute,
  feeKind: PaymentFeeKind,
  organizationId: string | null = null,
): FeeScope {
  const isCost = feeKind === PaymentFeeKind.PROVIDER_COST;
  return {
    provider_code: isCost ? route.provider_code : null,
    organization_id: isCost ? null : organizationId,
    rail: route.rail,
    country: route.country,
    currency: route.currency,
    direction: route.direction,
  };
}

export function sameScope(a: FeeScope, b: FeeScope): boolean {
  return SCOPE_DIMENSIONS.every((dimension) => (a[dimension] ?? null) === (b[dimension] ?? null));
}

export function routesCoveredBy(
  feeKind: PaymentFeeKind,
  scope: FeeScope,
  routes: ProviderRailRoute[],
): ProviderRailRoute[] {
  return routes.filter((route) => {
    const corridor = corridorScopeOf(route, feeKind, scope.organization_id);
    return SCOPE_DIMENSIONS.every(
      (dimension) => scope[dimension] == null || scope[dimension] === corridor[dimension],
    );
  });
}

export interface RemovalImpact {
  route: ProviderRailRoute;
  before: PaymentFee;
  /** `null`: nothing left to price the corridor, so it refuses operations. */
  after: PaymentFee | null;
}

/**
 * Resolved in the scope the removed rows belong to, so an organization's rows fall back for that
 * organization.
 */
export function removalImpact(
  removed: PaymentFee[],
  fees: PaymentFee[],
  routes: ProviderRailRoute[],
  now: number,
): RemovalImpact[] {
  if (removed.length === 0) return [];
  const { fee_kind: feeKind, organization_id: organizationId } = removed[0];
  const ids = new Set(removed.map((fee) => fee.id));
  const remaining = fees.filter((fee) => !ids.has(fee.id));
  return routes.flatMap((route) => {
    const query = feeQueryFor(route, feeKind, organizationId);
    const before = resolveFee(query, fees, now);
    if (!before || !ids.has(before.id)) return [];
    return [{ route, before, after: resolveFee(query, remaining, now) }];
  });
}

export function corridorsGoingDark(
  fee: PaymentFee,
  fees: PaymentFee[],
  routes: ProviderRailRoute[],
  now: number,
): ProviderRailRoute[] {
  return removalImpact([fee], fees, routes, now)
    .filter((impact) => impact.after == null)
    .map((impact) => impact.route);
}

/**
 * A live rule strictly more specific than `scope` that overlaps it — the new rule will never apply where that
 * one matches. Surfaced as a warning while authoring so a "platform default" is not written in the belief
 * that it covers an organization that already has its own price.
 */
export function findShadowingRule(
  feeKind: PaymentFeeKind,
  scope: FeeScope,
  fees: PaymentFee[],
  now: number,
): PaymentFee | null {
  const target = specificityOf(scope);
  return (
    fees.find((fee) => {
      if (fee.fee_kind !== feeKind || !isFeeLive(fee, now)) return false;
      const rowScope = feeScopeOf(fee);
      if (specificityOf(rowScope) <= target) return false;
      const compatible = SCOPE_DIMENSIONS.every(
        (dimension) =>
          scope[dimension] == null || rowScope[dimension] == null || rowScope[dimension] === scope[dimension],
      );
      const narrower = SCOPE_DIMENSIONS.some(
        (dimension) => scope[dimension] == null && rowScope[dimension] != null,
      );
      return compatible && narrower;
    }) ?? null
  );
}

/** Next version number the backend will assign to this exact scope (`latest + 1`, starting at 1). */
export function nextVersionFor(feeKind: PaymentFeeKind, scope: FeeScope, fees: PaymentFee[]): number {
  const key = scopeKey(feeKind, scope);
  const versions = fees
    .filter((fee) => !fee.deleted_at && scopeKey(fee.fee_kind, feeScopeOf(fee)) === key)
    .map((fee) => fee.version);
  return (versions.length ? Math.max(...versions) : 0) + 1;
}

/**
 * True when a live row of this exact scope already starts at this instant — the `fee_exists` 400,
 * pre-checked.
 */
export function hasValidFromCollision(
  feeKind: PaymentFeeKind,
  scope: FeeScope,
  validFromIso: string | null,
  fees: PaymentFee[],
): boolean {
  if (!validFromIso) return false;
  const instant = Date.parse(validFromIso);
  if (Number.isNaN(instant)) return false;
  const key = scopeKey(feeKind, scope);
  return fees.some(
    (fee) =>
      !fee.deleted_at &&
      scopeKey(fee.fee_kind, feeScopeOf(fee)) === key &&
      Date.parse(fee.valid_from) === instant,
  );
}
